import React, { useState } from 'react';
import { getTypeIcon, getPriorityColor, getStatusColor } from './boardUtils';
import useIsMobile from '../../hooks/useIsMobile';
import { 
  FolderIcon, 
  DocumentIcon,
  ChevronUpIcon,
  ChevronUpDownIcon
} from '@heroicons/react/24/outline';

const ListView = ({ tickets, onTicketClick }) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const isMobile = useIsMobile();

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alana tıklandıysa direction'ı değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Farklı alana tıklandıysa yeni field'ı seç ve asc ile başla
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Ticket'ları sırala
  const sortedTickets = [...tickets].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;

    switch (sortField) {
      case 'name':
        aValue = a.title?.toLowerCase() || '';
        bValue = b.title?.toLowerCase() || '';
        break;
      case 'type':
        aValue = (a.type?.type_name || a.type || '').toLowerCase();
        bValue = (b.type?.type_name || b.type || '').toLowerCase();
        break;
      case 'status':
        aValue = (a.status?.status_name || a.status || '').toLowerCase();
        bValue = (b.status?.status_name || b.status || '').toLowerCase();
        break;
      case 'priority':
        // Priority için özel sıralama (Critical > High > Medium > Low)
        const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityOrder[(a.priority?.priority_name || a.priority || '').toLowerCase()] || 0;
        bValue = priorityOrder[(b.priority?.priority_name || b.priority || '').toLowerCase()] || 0;
        break;
      case 'assignee':
        aValue = (a.assigned_to_user ? `${a.assigned_to_user.first_name} ${a.assigned_to_user.last_name}`.trim() : 'Unassigned').toLowerCase();
        bValue = (b.assigned_to_user ? `${b.assigned_to_user.first_name} ${b.assigned_to_user.last_name}`.trim() : 'Unassigned').toLowerCase();
        break;
      case 'updated':
        aValue = new Date(a.updated_at || a.updated || 0);
        bValue = new Date(b.updated_at || b.updated || 0);
        break;
      default:
        return 0;
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Sort icon'unu göster
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUpDownIcon className="w-3 h-3 text-gray-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-3 h-3 text-blue-500" /> : 
      <ChevronUpIcon className="w-3 h-3 text-blue-500 transform rotate-180" />;
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow">
      {isMobile ? (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {sortedTickets.map((ticket) => {
            const safeTicket = {
              id: String(ticket.id || ''),
              title: String(ticket.title || 'Untitled'),
              status: String(ticket.status?.status_name || ticket.status || 'To Do'),
              priority: String(ticket.priority?.priority_name || ticket.priority || 'Medium'),
              updated: String(ticket.updated_at || ticket.updated || 'N/A'),
            };

            return (
              <button
                key={safeTicket.id}
                type="button"
                onClick={() => onTicketClick(ticket)}
                className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700/60"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 truncate">{safeTicket.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">#{safeTicket.id}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(safeTicket.priority)}`}>
                    {safeTicket.priority}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(safeTicket.status)}`}>
                    {safeTicket.status}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{safeTicket.updated.split('T')[0]}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
        <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <div 
            className="col-span-3 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('name')}
          >
            Name {getSortIcon('name')}
          </div>
          <div 
            className="col-span-2 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('type')}
          >
            Type {getSortIcon('type')}
          </div>
          <div 
            className="col-span-2 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('status')}
          >
            Status {getSortIcon('status')}
          </div>
          <div 
            className="col-span-2 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('priority')}
          >
            Priority {getSortIcon('priority')}
          </div>
          <div 
            className="col-span-2 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('assignee')}
          >
            Assignee {getSortIcon('assignee')}
          </div>
          <div 
            className="col-span-1 text-right flex items-center justify-end cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            onClick={() => handleSort('updated')}
          >
            Updated {getSortIcon('updated')}
          </div>
        </div>
      </div>


      {/* Tickets List */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {sortedTickets.map(ticket => {
          // Güvenli ticket verisi elde et - backend'den gelen kompleks objeler için
          const safeTicket = {
            id: String(ticket.id || ''),
            title: String(ticket.title || 'Untitled'),
            status: String(ticket.status?.status_name || ticket.status || 'To Do'),
            priority: String(ticket.priority?.priority_name || ticket.priority || 'Medium'),
            type: String(ticket.type?.type_name || ticket.type || 'Task'),
            updated: String(ticket.updated_at || ticket.updated || 'N/A'),
            assignee: ticket.assigned_to_user || null
          };
          
          return (
            <div 
              key={safeTicket.id}
              onClick={() => onTicketClick(ticket)}
              className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/60 cursor-pointer transition-colors"
            >
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* Name Column */}
              <div className="col-span-3 flex items-center min-w-0">
                <div className="mr-2 flex-shrink-0">
                  {safeTicket.type === 'Bug' ? (
                    <DocumentIcon className="w-4 h-4 text-red-500" />
                  ) : safeTicket.type === 'Story' ? (
                    <FolderIcon className="w-4 h-4 text-blue-500" />
                  ) : safeTicket.type === 'Task' ? (
                    <DocumentIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <DocumentIcon className="w-4 h-4 text-purple-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate">
                    {safeTicket.title}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {safeTicket.id}
                  </div>
                </div>
              </div>

              {/* Type Column */}
              <div className="col-span-2">
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {safeTicket.type}
                </span>
              </div>

              {/* Status Column */}
              <div className="col-span-2">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(safeTicket.status)}`}>
                  {safeTicket.status}
                </span>
              </div>

              {/* Priority Column */}
              <div className="col-span-2">
                <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(safeTicket.priority)}`}>
                  {safeTicket.priority}
                </span>
              </div>

              {/* Assignee Column */}
              <div className="col-span-2 flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs mr-2">
                  {safeTicket.assignee ? 
                    safeTicket.assignee.first_name?.charAt(0) || '?'
                    : '?'
                  }
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {safeTicket.assignee ? 
                    `${safeTicket.assignee.first_name || ''} ${safeTicket.assignee.last_name || ''}`.trim() || 'Atanmamış'
                    : 'Atanmamış'
                  }
                </span>
              </div>

              {/* Updated Column */}
              <div className="col-span-1 text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {safeTicket.updated.split('T')[0]}
                </div>
              </div>
            </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {sortedTickets.length} tickets
          {sortField && (
            <span className="ml-2 text-blue-500">
              • Sorted by {sortField} ({sortDirection === 'asc' ? '↑' : '↓'})
            </span>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default ListView;
